import { ArrowUpRightIcon } from '@heroicons/react/24/solid';
import { Link } from '@remix-run/react';
import { GQLContentTilesFragment } from '@theprintbar/graphql';
import { BrandColorToTailwind, cn, getColourBackground } from '@theprintbar/shared';
import { Carousel, ImageContainer, OutlineButton, RichText } from '@theprintbar/ui';
import { useState } from 'react';

interface ContentTilesProps {
	content: GQLContentTilesFragment;
	rounded?: boolean;
	carouselKey?: string;
}

export function ContentTiles({ content, rounded, carouselKey = 'ContentTiles' }: ContentTilesProps) {
	const tiles = content.contentTiles.structure;
	const title = content.contentTilesContainer?.structure?.title;
	if (!tiles?.length) return null;

	const [expandedText, setExpandedText] = useState(Array(tiles?.length).fill(false));

	if (!tiles?.length) return null;

	const toggleText = (index: number) => {
		const updatedExpandedText = [...expandedText];
		updatedExpandedText[index] = !updatedExpandedText[index];
		setExpandedText(updatedExpandedText);
	};

	return (
		<>
			{title ? (
				<Carousel
					carouselKey={carouselKey}
					slidesPerView={1.4}
					breakpoints={{
						768: {
							slidesPerView: 2.4,
						},
						1024: {
							slidesPerView: rounded ? 2.4 : 3.4,
						},
						1280: {
							slidesPerView: rounded ? 3 : 4,
						},
						1536: {
							slidesPerView: rounded ? (tiles?.length < 4 ? 3 : 4) : 4,
						},
					}}
					slides={tiles?.map((cta, index) => {
						if (!cta) return null;

						const { to, text } = cta.links?.[0] || {};
						return {
							containerProps: {
								className: cn('pb-8 px-4 lg:px-8'),
								style: { height: 'auto' },
							},
							content: (
								<div
									className={cn(
										'mt-2 rounded-3xl border border-blue flex flex-col overflow-hidden h-full lg:hover:-translate-y-2.5 transition-all',
										rounded && 'border-none rounded-none',
										cta.colour && getColourBackground(cta.colour),
									)}
								>
									<ImageContainer image={cta.image} className={cn('h-64', rounded && 'rounded-3xl')} />
									<div className={cn('flex-3 p-8 border-t border-blue space-y-3', rounded && 'border-none !p-0 !pt-6')}>
										<h4
											className={cn(
												'text-base lg:text-lg uppercase',
												rounded && 'font-display font-light text-lg lg:text-xl',
											)}
										>
											{cta.title}
										</h4>
										<div
											className={cn(
												'text-sm lg:text-base',
												expandedText[index] || !!to ? '' : 'line-clamp-3 h-[60px]',
											)}
										>
											<RichText className="space-y-2" {...cta.text} />
										</div>

										{to ? (
											<OutlineButton icon={ArrowUpRightIcon}>
												<h5 className="text-xs uppercase mt-auto">{text || 'Read More'}</h5>
											</OutlineButton>
										) : cta.text ? (
											<button onClick={() => toggleText(index)}>
												<h6 className="text-xs capitalize">
													{expandedText[index] ? '- Read Less' : '+ Read More'}
												</h6>
											</button>
										) : null}
									</div>
								</div>
							),
						};
					})}
				/>
			) : (
				<div className={'grid grid-cols-2 lg:grid-cols-4 px-4 lg:px-8 gap-4 lg:gap-8'}>
					{tiles?.map((cta, index) => {
						if (!cta) return null;

						const { to, text, isPrimary } = cta.links?.[0] || {};
						const tile = (
							<div
								key={cta.title}
								className={cn(
									'mt-2 rounded-3xl border border-blue flex flex-col overflow-hidden h-full lg:hover:-translate-y-2.5 transition-all',
									rounded && 'border-none rounded-none',
								)}
							>
								<ImageContainer image={cta.image} className={cn('h-64', rounded && 'rounded-3xl')} />
								<div className={cn('flex-3 p-8 border-t border-blue space-y-3', rounded && 'border-none !p-0 !pt-6')}>
									<h4
										className={cn(
											'text-base lg:text-lg uppercase',
											rounded && 'font-display font-light text-lg lg:text-xl',
										)}
									>
										{cta.title}
									</h4>
									<div className={cn('text-sm lg:text-base', expandedText[index] ? '' : 'line-clamp-3 h-24')}>
										<RichText className="space-y-2" {...cta.text} />
									</div>

									{isPrimary ? (
										<OutlineButton icon={ArrowUpRightIcon}>
											<h5 className="text-xs uppercase">{text || 'Read More'}</h5>
										</OutlineButton>
									) : (
										<button onClick={() => toggleText(index)}>
											<h6 className="text-xs capitalize">{expandedText[index] ? '- Read Less' : '+ Read More'}</h6>
										</button>
									)}
								</div>
							</div>
						);

						return to ? <Link to={to}>{tile}</Link> : tile;
					})}
				</div>
			)}
		</>
	);
}
